/* eslint-disable */
import React, { useContext } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { SessionContext } from '../../../../context/sessionContext';
import Logger from '../../../../logger';
import Loader from '../../common/loader/loader';
import i18n from '../../../../localization/i18n';
import * as rk from '../../../../localization/resourceKeys';
import Checkbox from '../../common/checkbox/checkbox';
import BannerNotification from '../../common/banner/bannerNotification';
import { StepLayout } from '../../common/steplayout/StepLayout';
import { useMFAVerify } from './useMFAVerify';
import CodeInput from './CodeInput';

interface SetupAuthenticatorAppProps {
    onChangeMethod: () => void;
    secretURI: string;
    secret: string;
    triggerError?: string | null;
    triggerAccountLocked?: boolean;
    triggerLoading?: boolean;
}

const SetupAuthenticatorApp: React.FC<SetupAuthenticatorAppProps> = ({
    onChangeMethod,
    secretURI,
    secret,
    triggerError,
    triggerAccountLocked = false,
    triggerLoading = false,
}) => {
    const { sessionDetails } = useContext(SessionContext)!;
    const {
        code,
        isError,
        accountLocked,
        failedAttempts,
        submitPending,
        rememberDevice,
        verifyError,
        inputRef,
        handleCodeChange,
        handleVerify,
        setRememberDevice,
    } = useMFAVerify('SetupAuthenticatorApp');

    const isLocked = accountLocked || triggerAccountLocked;
    const isBusy = submitPending || triggerLoading;
    const isInteractionDisabled = isBusy || isLocked;
    const showTriggerError = !!triggerError && !isLocked;
    const showErrorBanner = isError && !isLocked && !showTriggerError;
    const showLockoutBanner = isLocked;
    const showWarningBanner = failedAttempts >= 2 && !isLocked;
    const verifyButtonDisabled = code.length < 6 || isInteractionDisabled;
    const warningTitle = `${i18n.t(rk.TITLE_LOCKOUT_WARNING1)} ${5 - failedAttempts}
             ${failedAttempts === 4 ? i18n.t(rk.TITLE_LOCKOUT_WARNING3) : i18n.t(rk.TITLE_LOCKOUT_WARNING2)}`;

    return (
        <StepLayout
            title={<h2 className='u-mb-small'>{i18n.t(rk.TITLE_MYVW_MFA_SELECT)}</h2>}
        >
            <h4 className="u-text-subtitle u-mb-xsmall">{i18n.t(rk.TITLE_ADD_ACCOUNT_AND_ENTER_CODE)}</h4>
            <Loader isLoading={isBusy} />
            {showLockoutBanner && (
                <div className="u-mt" data-testid="mfa-locked-banner">
                    <BannerNotification notificationStyle="error" title={i18n.t(rk.TITLE_LOCKOUT_ERROR)}>
                        {i18n.t(rk.TEXT_LOCKOUT_ERROR_MFA) as any}
                    </BannerNotification>
                </div>
            )}
            {showTriggerError && (
                <div className="u-mt" data-testid="trigger-error-banner">
                    <BannerNotification notificationStyle="error">
                        {triggerError as any}
                    </BannerNotification>
                </div>
            )}
            {showWarningBanner && (
                <div className="u-mt" data-testid="mfa-warning-banner">
                    <BannerNotification
                        title={warningTitle}
                        notificationStyle="warning"
                    >
                        {i18n.t(rk.TEXT_LOCKOUT_WARNING_VERIFY) as any}
                    </BannerNotification>
                </div>
            )}
            <ol className='u-mt'>
                <li className='u-mb-xsmall'>{i18n.t(rk.TEXT_STEP_1_ADD_ACCOUNT_AND_ENTER_CODE)}</li>
                <li className='u-mb-xsmall'>{i18n.t(rk.TEXT_STEP_2_ADD_ACCOUNT_AND_ENTER_CODE)}</li>
                <li className='u-mb-xsmall'>
                    {i18n.t(rk.TEXT_STEP_3_ADD_ACCOUNT_AND_ENTER_CODE)}
                    <p className='u-mt-xsmall u-mb-xsmall'>
                        <b>{i18n.t(rk.TEXT_ACCOUNT_NAME_ADD_ACCOUNT_AND_ENTER_CODE)}</b>
                        {' '}{i18n.t(rk.TEXT_VW_ACCOUNT_NAME_ADD_ACCOUNT_AND_ENTER_CODE)}
                    </p>
                    <p className='u-mb-small'>
                        <b>{i18n.t(rk.TEXT_SECRET_KEY_ADD_ACCOUNT_AND_ENTER_CODE)}</b>
                        {' '}<span data-testid='setup-authenticator-secret'>{secret}</span>
                    </p>

                    {secretURI && <div className="u-mv" data-testid='setup-authenticator-qr'><QRCodeSVG value={secretURI} data-testid="qr-code" /></div>}
                </li>
                <li className='u-mb-xsmall'>{i18n.t(rk.TEXT_STEP_4_ADD_ACCOUNT_AND_ENTER_CODE)}</li>
            </ol>

            <CodeInput value={code} onChange={handleCodeChange} disabled={isInteractionDisabled} inputRef={inputRef} />
            {(showErrorBanner) && !isLocked && !showTriggerError && (
                <p
                    className="c-error-message"
                    style={{ fontSize: "12px", marginLeft: "0px" }}
                >

                    {i18n.t(rk.ERROR_INVALID_MFA_CODE)}
                </p>
            )}
            <div className="u-mv-large">
                <Checkbox
                    disabled={isInteractionDisabled}
                    label={i18n.t(rk.REMEMBER_DEVICE)}
                    id="rememberDevice"
                    onChange={() => {
                        setRememberDevice(prev => {
                            Logger.info(
                                `${!prev ? 'Checked' : 'Unchecked'} remember this device checkbox`,
                                'Location ==> myVWMfa [SetupAuthenticatorApp]',
                                sessionDetails
                            );
                            return !prev;
                        });
                    }}
                    checked={rememberDevice}
                    testid="remember-device-checkbox"
                />
            </div>
            <div className="o-button-container o-button-container--center u-mt-xlarge">
                <button
                    className="c-btn c-btn--primary o-button-container__button"
                    onClick={handleVerify}
                    disabled={verifyButtonDisabled}
                    data-testid="app-mfa-verify"
                >
                    <span className="c-btn__text">{i18n.t(rk.BUTTON_VERIFY)}</span>
                </button>
            </div>
            <div className="o-button-container o-button-container--center u-mt-xlarge">
                <button
                    className="c-link o-button-container__button--center"
                    onClick={onChangeMethod}
                    disabled={isBusy}
                    data-testid="app-mfa-change-method"
                >
                    <span className="c-btn__text">{i18n.t(rk.LINK_CHANGE_VERIFICATION_METHOD)}</span>
                </button>
            </div>
        </StepLayout>
    );
};

export default SetupAuthenticatorApp;

